// @ts-strict-ignore
import { ApiQueryName } from "@/models/api"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import type { UseInfiniteQueryOptions } from "@tanstack/react-query"
import { useInfiniteQuery } from "@tanstack/react-query"

import { getSiteExplorer } from "./get-site-explorer"
import type {
  SiteExplorerPaginatedData,
  SiteExplorerParams,
} from "./site-explorer.types"

interface UseGetSiteExplorerOptions {
  initialPage?: number
  parameters: SiteExplorerParams
  queryOptions?: Omit<
    UseInfiniteQueryOptions<SiteExplorerPaginatedData, HttpError>,
    | "queryKey"
    | "queryFn"
    | "getNextPageParam"
    | "getPreviousPageParam"
    | "initialPageParam"
    | "select"
  >
}

/**
 * Returns a list of site explorer entries
 *
 * @example
 * const {
 *   siteExplorerData,
 *   siteExplorerError,
 *   siteExplorerIsError
 *   siteExplorerIsPending,
 * } = useGetSiteExplorer({
 *   parameters: { query: { org_id: 123 } },
 *   queryOptions // react query options
 * })
 */
export const useGetSiteExplorer = ({
  initialPage,
  parameters,
  queryOptions,
}: UseGetSiteExplorerOptions) => {
  const queryKey = useQueryKey(
    ApiQueryName.SiteExplorer,
    "getMany",
    null,
    parameters
  )

  const queryResult = useInfiniteQuery<SiteExplorerPaginatedData, HttpError>({
    ...queryOptions,
    queryKey,
    queryFn: async ({ pageParam }) => {
      const newParameters = {
        ...parameters,
      }
      newParameters.query["page[number]"] = pageParam as number
      return getSiteExplorer(newParameters)
    },
    initialPageParam: initialPage ?? 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page === lastPage.pages) {
        return undefined
      }
      return lastPage.page + 1
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page === 1) {
        return undefined
      }
      return firstPage.page - 1
    },
    throwOnError: resolveQueryThrowOnError(queryOptions?.throwOnError),
  })

  return useRenameQueryResult(queryResult, ApiQueryName.SiteExplorer)
}
